import {
  OPERATORS,
  DIFFICULTY_CONFIG,
  ENCOURAGING_MESSAGES,
  TRY_AGAIN_MESSAGES,
} from '../config/gameConfig';

/**
 * Get available operations based on user selection
 * @param {Object} selectedOperations - Object with plus, minus, gange boolean flags
 * @returns {Array} Array of operator symbols
 */
export function getAvailableOperations(selectedOperations) {
  const ops = [];
  if (selectedOperations.plus) ops.push(OPERATORS.PLUS);
  if (selectedOperations.minus) ops.push(OPERATORS.MINUS);
  if (selectedOperations.gange) ops.push(OPERATORS.MULTIPLY);
  return ops.length > 0 ? ops : [OPERATORS.PLUS];
}

/**
 * Generate a random number within range (inclusive)
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {number} Random number between min and max
 */
function randomInRange(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Generate a math problem based on difficulty and selected operations
 * @param {string} difficulty - Difficulty level
 * @param {Object} selectedOperations - Selected operations object
 * @returns {Object} Problem object with num1, num2, operator
 */
export function generateProblem(difficulty, selectedOperations) {
  const availableOps = getAvailableOperations(selectedOperations);
  const operator = availableOps[Math.floor(Math.random() * availableOps.length)];
  const config = DIFFICULTY_CONFIG[difficulty];

  let num1, num2;

  if (operator === OPERATORS.MULTIPLY) {
    // Multiplication
    num1 = randomInRange(config.multiplication.min, config.multiplication.max);
    const multiplierMax = config.multiplication.multiplierMax || config.multiplication.max;
    const multiplierMin = config.multiplication.multiplierMin || config.multiplication.min;
    num2 = randomInRange(multiplierMin, multiplierMax);
  } else {
    // Addition or Subtraction
    const { num1Min, num1Max, num2Min, num2Max } = config.additionSubtraction;
    num1 = randomInRange(num1Min, num1Max);
    num2 = randomInRange(num2Min, num2Max);

    // For subtraction, ensure num1 >= num2 (no negative results)
    if (operator === OPERATORS.MINUS && num2 > num1) {
      [num1, num2] = [num2, num1];
    }
  }

  return { num1, num2, operator };
}

/**
 * Calculate the correct answer for a problem
 * @param {Object} problem - Problem object with num1, num2, operator
 * @returns {number} The correct answer
 */
export function getCorrectAnswer(problem) {
  const { num1, num2, operator } = problem;
  if (operator === OPERATORS.PLUS) return num1 + num2;
  if (operator === OPERATORS.MINUS) return num1 - num2;
  if (operator === OPERATORS.MULTIPLY) return num1 * num2;
  return 0;
}

/**
 * Calculate points earned for a correct answer
 * @param {string} difficulty - Difficulty level
 * @param {number} streak - Current streak count
 * @returns {number} Points earned
 */
export function calculatePoints(difficulty, streak) {
  const config = DIFFICULTY_CONFIG[difficulty];
  const basePoints = config.basePoints;
  const streakBonus = streak >= 5 ? config.streakBonus : 0;
  return basePoints + streakBonus;
}

/**
 * Get a random encouraging message
 * @returns {string} Random encouraging message
 */
export function getRandomEncouragingMessage() {
  return ENCOURAGING_MESSAGES[
    Math.floor(Math.random() * ENCOURAGING_MESSAGES.length)
  ];
}

/**
 * Get a random try again message
 * @returns {string} Random try again message
 */
export function getRandomTryAgainMessage() {
  return TRY_AGAIN_MESSAGES[
    Math.floor(Math.random() * TRY_AGAIN_MESSAGES.length)
  ];
}

/**
 * Check if answer triggers an achievement
 * @param {number} newStreak - The new streak value
 * @param {number} level - Current level
 * @returns {Array} Array of achievement messages
 */
export function checkAchievements(newStreak, level) {
  const achievements = [];

  if (newStreak === 5) {
    achievements.push('Fem i træk! 🔥');
  }
  if (newStreak === 10) {
    achievements.push('Ti i træk! ⭐');
  }
  if (newStreak % 8 === 0) {
    achievements.push(`Niveau ${level + 1}! 🚀`);
  }

  return achievements;
}
